import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Modal, Tabs, Input, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useJ212Mode } from './hooks/useJ212Mode';
import { useJ212Record } from './hooks/useJ212Record';
import { SharedActionBar } from '../../components/SharedActionBar';
import { useModulePermission } from '../../hooks/usePermission';
import Tab1AlbumInfo from './components/Tab1AlbumInfo';
import Tab2Details from './components/Tab2Details';
import './J212Form.css';
import { BatchAcceptanceBridge, pwj212f1AcceptanceManifest } from '../../features/acceptance';
import { j212Api } from '../../services/j212Api';

const J212Form: React.FC = () => {
  const navigate = useNavigate();
  const { mode, isEditing, enterAdd, enterEdit, enterBrowse } = useJ212Mode();
  const {
    records,
    current,
    currentIdx,
    formData,
    loading,
    emptyDetail,
    setField,
    navFirst,
    navPrev,
    navNext,
    navLast,
    startAdd,
    startEdit,
    cancel,
    save,
    doDelete,
    quickSearch,
  } = useJ212Record(mode, enterBrowse);

  const { canCreate, canEdit, canDelete } = useModulePermission('pwj212f1');

  const [activeTab, setActiveTab] = useState('tab1');
  const [selectedRowIdx, setSelectedRowIdx] = useState(0);
  const [searchText, setSearchText] = useState('');

  const handleSearch = useCallback(() => {
    if (searchText.trim()) quickSearch(searchText.trim());
  }, [searchText, quickSearch]);

  const handleAdd = useCallback(() => {
    startAdd();
    enterAdd();
    setActiveTab('tab1');
  }, [startAdd, enterAdd]);

  const handleEdit = useCallback(() => {
    startEdit();
    enterEdit();
    setActiveTab('tab1');
  }, [startEdit, enterEdit]);

  const handleCancel = useCallback(() => {
    cancel();
  }, [cancel]);

  const handleDelete = useCallback(() => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除銷售專輯「${current.fj210_01}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  }, [current, doDelete]);

  const tabItems = [
    {
      key: 'tab1',
      label: '基本資料',
      children: (
        <Tab1AlbumInfo
          formData={formData}
          isEditing={isEditing}
          isEditMode={mode === 'edit'}
          setField={setField}
        />
      ),
    },
    {
      key: 'tab2',
      label: `產品明細 (${formData.details.length})`,
      children: (
        <Tab2Details
          formData={formData}
          details={formData.details}
          isEditing={isEditing}
          selectedRowIdx={selectedRowIdx}
          onSelectRow={setSelectedRowIdx}
          setField={setField}
          emptyDetail={emptyDetail}
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className="j212-form">
        {/* Title */}
        <div className="j212-header">
          <span className="j212-title">銷售專輯製作 (pwj212f1)</span>
          <span className="j212-meta">
            {current?.fj210_01 || '(新增)'}
          </span>
        </div>

        {/* QuickSearch bar — FoxPro Pwtqseek1 */}
        <div className="j212-search-bar">
          <Input
            placeholder="專輯編號查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            disabled={isEditing}
            style={{ width: 200 }}
            allowClear
          />
          <Button
            icon={<SearchOutlined />}
            onClick={handleSearch}
            disabled={isEditing}
          >
            查詢
          </Button>
        </div>

        {/* Tabs */}
        <div className="j212-tabs-container">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="small"
          />
        </div>

        {/* Spacer for fixed bottom bar */}
        <div style={{ height: 64 }} />

        {/* SharedActionBar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!current}
          currentIdx={currentIdx}
          recordCount={records.length}
          onNavFirst={navFirst}
          onNavPrev={navPrev}
          onNavNext={navNext}
          onNavLast={navLast}
          onAdd={handleAdd}
          onModify={handleEdit}
          onDelete={handleDelete}
          onSave={save}
          onCancel={handleCancel}
          onExit={() => navigate('/erp')}
          canCreate={canCreate}
          canEdit={canEdit}
          canDelete={canDelete}
          acceptanceActionIds={{ create: 'PWJ212-ACT-SAVE', update: 'PWJ212-ACT-SAVE' }}
        />
        <BatchAcceptanceBridge
          manifest={pwj212f1AcceptanceManifest}
          records={records}
          selectedKeys={current?.fj210_01 ? [current.fj210_01] : []}
          keyOf={(item) => item.fj210_01}
          queryExact={(key) => j212Api.getByID(key)}
          lastApiResponse={current}
          loading={loading}
          onAutoFill={navFirst}
          autoFillDisabled={records.length === 0}
          screenDraft={formData}
        />
      </div>
    </Spin>
  );
};

export default J212Form;
